import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API_BASE_URL from '../config';
import styles from '../css-isolated/consultwizard.module.css';
import ConsultSymptoms from './consult_symptoms';
import ConsultMedication from './consult_medication';
import ConsultFollowup from './consult_followup';
import NotificationBell from './notification';
import ChangePasswordModal from './change_pass';
import authFetch from '../utils/authFetch';
import { lettersOnly } from '../utils/inputValidation';

function ConsultWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get edit mode data from navigation state
  const { mode, initialData, consultationData, initialView, followupDate } = location.state || {};
  const isEditMode = mode === 'edit';
  
  // System header time state
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userData, setUserData] = useState(null);

  // Update time every second for system header
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load user data from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUserData(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Load selected PDL from localStorage
  useEffect(() => {
    const storedPDL = localStorage.getItem('selectedPDL');
    if (storedPDL) {
      try {
        const pdlData = JSON.parse(storedPDL);
        setSelectedPDLDisplay(pdlData);
        setSelectedHrInfoId(pdlData.id);
        
        // Auto-fill symptoms form with PDL data when coming from follow-up
        if (location.state?.autoShowSymptoms) {
          setSymptomsData(prev => ({
            ...prev,
            lastName: pdlData.lastName || '',
            firstName: pdlData.firstName || '',
            middleName: pdlData.middleName || '',
            aka: pdlData.aka || '',
            age: pdlData.age || ''
          }));
          
          // Switch to symptoms view
          setCurrentView('symptoms');
        }
      } catch (error) {
        console.error('Error parsing selected PDL:', error);
      }
    }
  }, [location.state]);

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };
  
  const toggleDarkMode = () => {
    setPendingTheme(!isDarkMode);
    setShowThemeModal(true);
  };

  const confirmThemeChange = async () => {
    setShowThemeModal(false);
    
    try {
      setIsDarkMode(pendingTheme);
      localStorage.setItem('theme', pendingTheme ? 'dark' : 'light');
      
      if (userData && userData.id) {
        const response = await authFetch(`${API_BASE_URL}/api/update-theme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userData.id,
            is_darkmode: pendingTheme ? 1 : 0
          }),
        });
        
        if (!response.ok) {
          console.error('Failed to update theme in database');
        }
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    } finally {
      setPendingTheme(null);
    }
  };

  const cancelThemeChange = () => {
    setShowThemeModal(false);
    setPendingTheme(null);
  };

  const confirmLogout = () => {
    localStorage.removeItem('user');
    setShowLogoutModal(false);
    navigate('/login');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(`.${styles.profileSection}`)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  // Initialize form data when in edit mode
  useEffect(() => {
    if (isEditMode && (initialData || consultationData)) {
      const data = initialData || consultationData;
      // Set the view to symptoms form first when editing
      setCurrentView('symptoms');
      
      // Format date for HTML date input (YYYY-MM-DD)
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        
        try {
          // Handle various date formats
          let date;
          if (dateString.includes('/')) {
            // Handle MM/DD/YYYY or DD/MM/YYYY format
            const parts = dateString.split('/');
            if (parts.length === 3) {
              // Assume MM/DD/YYYY format
              date = new Date(parts[2], parts[0] - 1, parts[1]);
            }
          } else if (dateString.includes('-')) {
            // Handle YYYY-MM-DD format or similar
            date = new Date(dateString);
          } else {
            date = new Date(dateString);
          }
          
          if (isNaN(date.getTime())) {
            console.warn('Invalid date:', dateString);
            return '';
          }
          
          // Format as YYYY-MM-DD for HTML date input
          return date.toISOString().split('T')[0];
        } catch (error) {
          console.error('Error formatting date:', dateString, error);
          return '';
        }
      };
      
      // Calculate age from date of birth
      const calculateAge = (dateOfBirth) => {
        if (!dateOfBirth) return '';
        
        try {
          const today = new Date();
          const birthDate = new Date(dateOfBirth);
          
          if (isNaN(birthDate.getTime())) {
            return '';
          }
          
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          
          return age.toString();
        } catch (error) {
          console.error('Error calculating age:', error);
          return '';
        }
      };
      
      // Get date of birth from hrInfo data
      const dateOfBirth = (data.hrInfo && data.hrInfo.date_of_birth) || data.date_of_birth;
      const calculatedAge = calculateAge(dateOfBirth);
      // Initialize symptoms data with better fallback handling
      setSymptomsData({
        lastName: data.lastName || data.last_name || (data.hrInfo && data.hrInfo.last_name) || '',
        firstName: data.firstName || data.first_name || (data.hrInfo && data.hrInfo.first_name) || '',
        middleName: data.middleName || data.middle_name || (data.hrInfo && data.hrInfo.middle_name) || '',
        aka: data.akaNickname || data.aka || data.alias_nickname || (data.hrInfo && data.hrInfo.alias_nickname) || '',
        age: calculatedAge,
        date: formatDateForInput(data.date || data.consultationDate),
        chiefComplaint: data.chief_complaint || data.chiefComplaint || '',
        diagnosis: data.diagnosis || ''
      });
      
      // Initialize medication data with better error handling
      const medications = data.medications || [];
      const medicationsList = medications.length > 0 
        ? medications.map(med => ({
            id: med.id || null, // Include medication ID for updates
            medicationName: med.name_of_medication || med.medicationName || '',
            dosage: med.dosage || '',
            quantity: med.quantity ? String(med.quantity) : ''
          }))
        : [{ id: null, medicationName: '', dosage: '', quantity: '' }];
      setMedicationData({
        medications: medicationsList,
        diagnostics: data.diagnostics || '',
        referral: data.referral || ''
      });

      // Set the initial hr_info_id for edit mode
      const hrInfoId = data.hr_info_id || (data.hrInfo && data.hrInfo.id);
      setSelectedHrInfoId(hrInfoId);
    }
  }, [isEditMode, initialData, consultationData]);

  // Navigation state - 'symptoms', 'medication' for consultation steps, 'search' for search functionality
  const [currentView, setCurrentView] = useState(
    initialView === 'followup' ? 'followup' : isEditMode ? 'symptoms' : 'search'
  );
  
  // Search functionality state
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchData, setSearchData] = useState({
    lastName: '',
    firstName: '',
    middleName: '',
    aka: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Selected PDL state for display
  const [selectedPDLDisplay, setSelectedPDLDisplay] = useState(null);

  // Symptoms form state - lifted up to persist across tab changes
  const [symptomsData, setSymptomsData] = useState({
    lastName: '',
    firstName: '',
    middleName: '',
    aka: '',
    age: '',
    date: '',
    chiefComplaint: '',
    diagnosis: ''
  });

  // Medication form state - lifted up to persist across tab changes
  const [medicationData, setMedicationData] = useState({
    medications: [{ id: null, medicationName: '', dosage: '', quantity: '' }],
    diagnostics: '',
    referral: ''
  });

  // Loading state for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successTitle, setSuccessTitle] = useState('');

  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Clear form confirmation modal state
  const [clearFormModal, setClearFormModal] = useState({
    show: false
  });

  // Validation modal state
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [missingFields, setMissingFields] = useState([]);

  // Search validation modal state
  const [showSearchValidationModal, setShowSearchValidationModal] = useState(false);

  // Selection success modal state
  const [showSelectionSuccessModal, setShowSelectionSuccessModal] = useState(false);
  const [selectionSuccessData, setSelectionSuccessData] = useState({
    name: '',
    message: ''
  });

  // Track the selected PDL's hr_info_id (can be different from original in edit mode)
  const [selectedHrInfoId, setSelectedHrInfoId] = useState(null);

  // PDL selection confirmation state
  const [showPDLConfirmModal, setShowPDLConfirmModal] = useState(false);
  const [selectedPDLForConfirm, setSelectedPDLForConfirm] = useState(null);
  const [pdlDetailsLoading, setPdlDetailsLoading] = useState(false);

  // Schedule checkup modal state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedPDLForSchedule, setSelectedPDLForSchedule] = useState(null);
  const [showScheduleSuccessModal, setShowScheduleSuccessModal] = useState(false);
  const [scheduleSuccessData, setScheduleSuccessData] = useState(null);

  // Follow-up consultation modal state
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  
  // Follow-up date picker modal state
  const [showFollowUpDateModal, setShowFollowUpDateModal] = useState(false);
  const [followUpCurrentDate, setFollowUpCurrentDate] = useState(new Date());
  const [followUpSelectedDates, setFollowUpSelectedDates] = useState([]);
  const [followUpRecords, setFollowUpRecords] = useState([]); // Store records with IDs
  
  // Theme State
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme !== 'light';
  });
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [pendingTheme, setPendingTheme] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  // Progress calculation functions with useMemo for optimal performance
  const searchProgress = useMemo(() => {
    // If PDL is already selected, search is 100% complete
    if (selectedHrInfoId) {
      return 100;
    }
    
    // Calculate progress based on search fields filled
    const searchFields = ['lastName', 'firstName', 'middleName', 'aka'];
    let filledFields = 0;
    
    searchFields.forEach(field => {
      if (searchData[field] && searchData[field].toString().trim() !== '') {
        filledFields++;
      }
    });
    
    // Simple calculation: each field = 25%, no bonus until PDL is selected
    // 0 fields = 0%, 1 field = 25%, 2 fields = 50%, 3 fields = 75%, 4 fields = 100%
    // But max is 95% until PDL is actually selected
    const progress = (filledFields / searchFields.length) * 100;
    return filledFields === 4 ? 95 : Math.round(progress); // 95% max until selection
  }, [selectedHrInfoId, searchData]);

  const symptomsProgress = useMemo(() => {
    const fields = ['lastName', 'firstName', 'date', 'chiefComplaint'];
    const optionalFields = ['middleName', 'aka', 'age', 'diagnosis'];
    
    let filledRequired = 0;
    let filledOptional = 0;
    
    fields.forEach(field => {
      if (symptomsData[field] && symptomsData[field].toString().trim() !== '') {
        filledRequired++;
      }
    });
    
    optionalFields.forEach(field => {
      if (symptomsData[field] && symptomsData[field].toString().trim() !== '') {
        filledOptional++;
      }
    });
    
    // Required fields worth 70%, optional fields worth 30%
    const requiredProgress = (filledRequired / fields.length) * 70;
    const optionalProgress = (filledOptional / optionalFields.length) * 30;
    
    return Math.round(requiredProgress + optionalProgress);
  }, [symptomsData]);

  const medicationProgress = useMemo(() => {
    let totalFields = 0;
    let filledFields = 0;
    
    // Check medications array
    if (medicationData.medications && medicationData.medications.length > 0) {
      medicationData.medications.forEach(med => {
        totalFields += 3; // medicationName, dosage, quantity
        if (med.medicationName && med.medicationName.trim() !== '') filledFields++;
        if (med.dosage && med.dosage.trim() !== '') filledFields++;
        if (med.quantity && med.quantity.trim() !== '') filledFields++;
      });
    } else {
      totalFields += 3; // At least one medication entry
    }
    
    // Check other fields (diagnostics, referral are optional, nodMdInitials is required)
    totalFields += 1; // nodMdInitials
    if (medicationData.nodMdInitials && medicationData.nodMdInitials.trim() !== '') filledFields++;
    
    // Optional fields - add some weight if filled
    if (medicationData.diagnostics && medicationData.diagnostics.trim() !== '') filledFields += 0.5;
    if (medicationData.referral && medicationData.referral.trim() !== '') filledFields += 0.5;
    
    if (totalFields === 0) return 0;
    
    return Math.min(100, Math.round((filledFields / totalFields) * 100));
  }, [medicationData]);

  const overallProgress = useMemo(() => {
    // 3-step progress with accurate fill tracking
    // Each step contributes equally (33.33% each)
    if (currentView === 'search') {
      // Show only search progress (0-33.33%)
      const progress = (searchProgress / 100) * 33.33;
      return Number(progress.toFixed(2));
    } else if (currentView === 'symptoms') {
      // Count search as complete only if PDL selected, otherwise show partial
      const searchContribution = selectedHrInfoId ? 33.33 : (searchProgress / 100) * 33.33;
      const symptomsContribution = (symptomsProgress / 100) * 33.33;
      const progress = searchContribution + symptomsContribution;
      return Number(progress.toFixed(2));
    } else if (currentView === 'medication') {
      // Count previous steps based on actual completion
      const searchContribution = selectedHrInfoId ? 33.33 : (searchProgress / 100) * 33.33;
      const symptomsContribution = (symptomsProgress / 100) * 33.33;
      const medicationContribution = (medicationProgress / 100) * 33.33;
      const progress = searchContribution + symptomsContribution + medicationContribution;
      return Number(progress.toFixed(2));
    }
    
    return 0;
  }, [currentView, searchProgress, symptomsProgress, medicationProgress, selectedHrInfoId]);

  // Search functionality handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    
    try {
      // Prepare search parameters to match backend API expectations
      const searchParams = new URLSearchParams();
      if (searchData.lastName) searchParams.append('lastName', searchData.lastName);
      if (searchData.firstName) searchParams.append('firstName', searchData.firstName);
      if (searchData.middleName) searchParams.append('middleName', searchData.middleName);
      if (searchData.aka) searchParams.append('akaNickname', searchData.aka);

      // API call to search hr_info table - if no params, it will return all records
      const response = await authFetch(`${API_BASE_URL}/api/hr-info/search?${searchParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const results = await response.json();
      
      // Transform the results to match the expected format
      // Exclude PDLs that have a final remark (released, transferred, deceased, etc.)
      const transformedResults = results
        .filter(item => !item.final_remarks)
        .map(item => ({
        id: item.id,
        last_name: item.last_name || item.surname,
        first_name: item.first_name || item.firstName,
        middle_name: item.middle_name || item.middleName,
        aka: item.alias_nickname || item.aka || 'N/A',
        date_of_birth: item.date_of_birth || item.dateOfBirth,
        current_cell: item.current_cell || item.currentCell
      }));
      
      setSearchResults(transformedResults);
      setIsLoading(false);
    } catch (error) {
      console.error('Error searching HR info records:', error);
      setSearchResults([]);
      setIsLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchData({
      lastName: '',
      firstName: '',
      middleName: '',
      aka: ''
    });
    setSearchResults([]);
    setCurrentPage(1);
  };

  // Pagination calculations
  const totalPages = Math.ceil(searchResults.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = searchResults.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSelectRecord = async (record) => {
    setPdlDetailsLoading(true);
    
    try {
      // Fetch complete PDL record from hr_info table with cell relationship
      const response = await authFetch(`${API_BASE_URL}/api/hr-info/${record.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const fullRecord = await response.json();
        // Set the selected PDL for confirmation modal
        setSelectedPDLForConfirm(fullRecord);
        setShowPDLConfirmModal(true);
      } else {
        console.error('Failed to fetch full PDL record');
        // Still show confirmation with basic info we have
        setSelectedPDLForConfirm(record);
        setShowPDLConfirmModal(true);
      }
    } catch (error) {
      console.error('Error fetching full PDL record:', error);
      // Show confirmation with basic record info we have
      setSelectedPDLForConfirm(record);
      setShowPDLConfirmModal(true);
    } finally {
      setPdlDetailsLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    
    try {
      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      
      if (isNaN(birthDate.getTime())) {
        return 'N/A';
      }
      
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return age.toString();
    } catch (error) {
      console.error('Error calculating age:', error);
      return 'N/A';
    }
  };

  const confirmPDLSelection = () => {
    if (!selectedPDLForConfirm) return;
    
    // Calculate age helper function
    const calculateFormAge = (dateOfBirth) => {
      if (!dateOfBirth) return '';
      
      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return age.toString();
    };
    
    // Store the selected PDL data in the format expected by consult_search
    const pdlDataForStorage = {
      id: selectedPDLForConfirm.id,
      fullName: `${selectedPDLForConfirm.first_name || ''} ${selectedPDLForConfirm.middle_name || ''} ${selectedPDLForConfirm.last_name || ''}`.trim(),
      alsoKnownAs: selectedPDLForConfirm.alias_nickname || selectedPDLForConfirm.aka || '',
      age: calculateFormAge(selectedPDLForConfirm.date_of_birth),
      currentCell: selectedPDLForConfirm.cell_no || selectedPDLForConfirm.cell?.cell_no || 'Unassigned',
      photo: selectedPDLForConfirm.photo_of_pdl || null
    };
    localStorage.setItem('selectedPDL', JSON.stringify(pdlDataForStorage));
    
    // Update the selected PDL display state immediately
    setSelectedPDLDisplay(pdlDataForStorage);
    
    // Update the selected hr_info_id
    setSelectedHrInfoId(selectedPDLForConfirm.id);
    // Auto-fill the symptoms form with selected record data
    const autoFilledSymptomsData = {
      ...symptomsData, // Preserve existing form data
      lastName: selectedPDLForConfirm.last_name || '',
      firstName: selectedPDLForConfirm.first_name || '',
      middleName: selectedPDLForConfirm.middle_name || '',
      aka: selectedPDLForConfirm.alias_nickname || selectedPDLForConfirm.aka || '',
      age: calculateFormAge(selectedPDLForConfirm.date_of_birth),
      // Keep other fields as they are (date, chiefComplaint, diagnosis)
    };
    
    // Update the symptoms form data
    setSymptomsData(autoFilledSymptomsData);
    
    // Close PDL confirmation modal
    setShowPDLConfirmModal(false);
    setSelectedPDLForConfirm(null);
    
    // Switch to symptoms view to show the auto-filled form
    setCurrentView('symptoms');
    
    // Show selection success modal with different message for edit mode
    setSelectionSuccessData({
      name: `${selectedPDLForConfirm.first_name} ${selectedPDLForConfirm.last_name}`,
      message: isEditMode 
        ? 'Consultation form has been updated with new patient information. The consultation will be reassigned to this patient when saved.'
        : 'Consultation form has been auto-filled with patient information.'
    });
    setShowSelectionSuccessModal(true);
  };

  const cancelPDLSelection = () => {
    setShowPDLConfirmModal(false);
    setSelectedPDLForConfirm(null);
  };

  // Clear selected PDL display
  const clearSelectedPDLDisplay = () => {
    localStorage.removeItem('selectedPDL');
    setSelectedPDLDisplay(null);
  };

  const handleScheduleCheckup = async (record) => {
    setPdlDetailsLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/hr-info/${record.id}`);
      if (response.ok) {
        const fullData = await response.json();
        setSelectedPDLForSchedule(fullData);
        
        // Fetch existing follow-up consultations and pre-populate selected dates
        try {
          const followUpResponse = await authFetch(`${API_BASE_URL}/api/followup-consult/patient/${record.id}`);
          if (followUpResponse.ok) {
            const followUpData = await followUpResponse.json();
            // Store full records with IDs
            setFollowUpRecords(followUpData);
            // Extract dates for display
            const dates = followUpData.map(item => new Date(item.date)).filter(date => !isNaN(date));
            setFollowUpSelectedDates(dates);
          } else {
            setFollowUpRecords([]);
            setFollowUpSelectedDates([]);
          }
        } catch (followUpError) {
          console.error('Error fetching follow-up consultations:', followUpError);
          setFollowUpRecords([]);
          setFollowUpSelectedDates([]);
        }
        
        setShowScheduleModal(true);
      }
    } catch (error) {
      console.error('Error fetching PDL details:', error);
      alert('Failed to load PDL details');
    } finally {
      setPdlDetailsLoading(false);
    }
  };

  const confirmScheduleCheckup = () => {
    // Close the PDL confirmation modal and open date picker
    setShowScheduleModal(false);
    setShowFollowUpDateModal(true);
  };

  const finalizeScheduleCheckup = async () => {
    if (followUpSelectedDates.length === 0) {
      alert('Please select at least one date for the checkup');
      return;
    }

    try {
      // Get current user from localStorage
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const currentUserId = currentUser.id;

      // Filter out dates that already exist in the database
      const existingDates = followUpRecords.map(record => new Date(record.date).toDateString());
      const newDates = followUpSelectedDates.filter(date => !existingDates.includes(date.toDateString()));

      // Only submit if there are new dates to add
      if (newDates.length > 0) {
        // Prepare follow-up consultation payload with only new dates
        const followUpPayload = {
          hr_info_id: selectedPDLForSchedule.id,
          dates: newDates.map(date => {
            // Format date as YYYY-MM-DD for the backend
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }),
          nod_md: currentUserId ? parseInt(currentUserId) : null
        };
        const response = await authFetch(`${API_BASE_URL}/api/followup-consult`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(followUpPayload),
        });

        if (!response.ok) {
          let errorDetails;
          try {
            errorDetails = await response.json();
          } catch {
            errorDetails = await response.text();
          }
          console.error('Schedule checkup submission error:', errorDetails);
          throw new Error('Failed to save scheduled checkup');
        }

        await response.json();
      } else {
      }

      // Store success data and show success modal
      setScheduleSuccessData({
        pdlName: `${selectedPDLForSchedule.first_name} ${selectedPDLForSchedule.last_name}`,
        dates: followUpSelectedDates
      });
      
      // Close date picker modal and reset state
      setShowFollowUpDateModal(false);
      setSelectedPDLForSchedule(null);
      setFollowUpSelectedDates([]);
      
      // Show success modal
      setShowScheduleSuccessModal(true);
    } catch (error) {
      console.error('Error scheduling checkup:', error);
      alert('Failed to schedule checkup. Please try again.');
    }
  };

  const cancelScheduleCheckup = () => {
    setShowScheduleModal(false);
    setSelectedPDLForSchedule(null);
  };

  const toggleDateSelection = async (date) => {
    const dateStr = date.toDateString();
    const exists = followUpSelectedDates.some(d => d.toDateString() === dateStr);
    
    if (exists) {
      // Check if this date has an existing record in the database
      const existingRecord = followUpRecords.find(record => {
        const recordDate = new Date(record.date || record.date_of_followup);
        return !isNaN(recordDate) && recordDate.toDateString() === dateStr;
      });
      
      if (existingRecord) {
        // Delete from database
        try {
          const response = await authFetch(`${API_BASE_URL}/api/followup-consult/${existingRecord.id}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            }
          });
          
          if (response.ok) {
            // Remove from records array
            setFollowUpRecords(prev => prev.filter(r => r.id !== existingRecord.id));
          } else {
            console.error('Failed to delete follow-up consultation');
            alert('Failed to delete the scheduled checkup');
            return; // Don't remove from UI if deletion failed
          }
        } catch (error) {
          console.error('Error deleting follow-up consultation:', error);
          alert('Error deleting the scheduled checkup');
          return; // Don't remove from UI if deletion failed
        }
      }
      
      // Remove from selected dates
      setFollowUpSelectedDates(prev => prev.filter(d => d.toDateString() !== dateStr));
    } else {
      // Add new date
      setFollowUpSelectedDates(prev => [...prev, date]);
    }
  };

  const clearAllSelectedDates = async () => {
    // Delete all existing records from database
    if (followUpRecords.length > 0) {
      try {
        const deletePromises = followUpRecords.map(record =>
          authFetch(`${API_BASE_URL}/api/followup-consult/${record.id}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            }
          })
        );
        
        await Promise.all(deletePromises);
        setFollowUpRecords([]);
      } catch (error) {
        console.error('Error deleting follow-up consultations:', error);
        alert('Error deleting some scheduled checkups');
      }
    }
    
    setFollowUpSelectedDates([]);
  };

  // Navigation handlers for consultation tabs
  const handleBack = () => {
    if (currentView === 'medication') {
      setCurrentView('symptoms');
    }
  };

  const handleNext = () => {
    if (currentView === 'symptoms') {
      setCurrentView('medication');
    }
  };

  // Clear form data function
  const clearFormData = () => {
    setClearFormModal({ show: true });
  };

  // Confirm clear form data
  const confirmClearFormData = () => {
    setSymptomsData({
      lastName: '',
      firstName: '',
      middleName: '',
      aka: '',
      age: '',
      date: '',
      chiefComplaint: '',
      diagnosis: ''
    });
    setMedicationData({
      medications: [{ id: null, medicationName: '', dosage: '', quantity: '' }],
      diagnostics: '',
      referral: '',
      nodMdInitials: ''
    });
    setClearFormModal({ show: false });
    setSuccessTitle('Form Cleared');
    setSuccessMessage('All consultation form data has been cleared successfully.');
    setShowSuccessModal(true);
  };

  // Cancel clear form data
  const cancelClearFormData = () => {
    setClearFormModal({ show: false });
  };

  // Validation functions
  const validateConsultationData = () => {
    const missing = [];

    // Validate symptoms data
    if (!symptomsData.date) missing.push({ field: 'Date', section: 'Consultation Symptoms' });
    if (!symptomsData.chiefComplaint) missing.push({ field: 'Chief Complaint', section: 'Consultation Symptoms' });

    // Validate medication data
    const hasIncompleteMedication = medicationData.medications.some(med => 
      !med.medicationName || !med.dosage
    );
    
    if (hasIncompleteMedication) {
      missing.push({ field: 'Medication Name and Dosage', section: 'Consultation Medication' });
    }

    // Check if PDL is selected (for create mode) or hr_info_id is available (for edit mode)
    if (!isEditMode) {
      // For create mode, check if PDL is selected
      if (!selectedHrInfoId) {
        let selectedPDL = {};
        try { selectedPDL = JSON.parse(localStorage.getItem('selectedPDL') || '{}'); } catch { /* corrupted */ }
        if (!selectedPDL.id) {
          missing.push({ field: 'PDL Selection', section: 'Patient Selection' });
        }
      }
    } else {
      // For edit mode, ensure we have a valid hr_info_id
      if (!selectedHrInfoId && !initialData?.hr_info_id && !consultationData?.hr_info_id) {
        missing.push({ field: 'Patient Information', section: 'Patient Selection' });
      }
    }

    return missing;
  };

  const showValidationError = (missing) => {
    setMissingFields(missing);
    setShowValidationModal(true);
  };

  const goToSection = (sectionName) => {
    if (sectionName === 'Consultation Symptoms') {
      setCurrentView('symptoms');
    } else if (sectionName === 'Consultation Medication') {
      setCurrentView('medication');
    } else if (sectionName === 'Patient Selection') {
      setCurrentView('search');
    }
    setShowValidationModal(false);
  };

  // Handle submit button click - show follow-up modal first
  const handleSubmitClick = () => {
    // Validate all required fields
    const missing = validateConsultationData();
    
    if (missing.length > 0) {
      showValidationError(missing);
      return;
    }

    // Show follow-up consultation modal
    setShowFollowUpModal(true);
  };

  // Handle follow-up modal response
  const handleFollowUpResponse = async (addFollowUp) => {
    setShowFollowUpModal(false);
    
    if (addFollowUp) {
      // Fetch existing follow-up consultations for this patient
      try {
        // Get hr_info_id - for edit mode use selectedHrInfoId or initialData, for create mode use selectedHrInfoId
        let hr_info_id;
        if (isEditMode) {
          hr_info_id = selectedHrInfoId || initialData?.hr_info_id || consultationData?.hr_info_id;
        } else {
          hr_info_id = selectedHrInfoId;
          if (!hr_info_id) {
            let selectedPDL = {};
            try { selectedPDL = JSON.parse(localStorage.getItem('selectedPDL') || '{}'); } catch { /* corrupted */ }
            hr_info_id = selectedPDL.id;
          }
        }

        if (hr_info_id) {
          const response = await authFetch(`${API_BASE_URL}/api/followup-consult/patient/${hr_info_id}`);
          if (response.ok) {
            const records = await response.json();
            // Store records with IDs
            setFollowUpRecords(records);
            
            // Pre-populate selected dates from existing records
            // Use 'date' field and filter out invalid dates
            const existingDates = records
              .map(record => new Date(record.date || record.date_of_followup))
              .filter(date => !isNaN(date));
            setFollowUpSelectedDates(existingDates);
          }
        }
      } catch (error) {
        console.error('Error fetching existing follow-up consultations:', error);
      }
      
      // Show date picker modal
      setShowFollowUpDateModal(true);
    } else {
      // Proceed with submission
      proceedWithSubmission();
    }
  };
  
  // Handle follow-up date selection
  const handleFollowUpDateConfirm = () => {
    // Check if this is for scheduling checkup or follow-up consultation
    if (selectedPDLForSchedule) {
      finalizeScheduleCheckup();
    } else {
      setShowFollowUpDateModal(false);
      setShowFollowUpModal(false);
      // Proceed with submission including the follow-up dates
      proceedWithSubmission(followUpSelectedDates);
    }
  };
  
  // Handle follow-up date modal close
  const handleFollowUpDateCancel = () => {
    setShowFollowUpDateModal(false);
    setFollowUpSelectedDates([]);
    // Clear schedule checkup state if it was for scheduling
    if (selectedPDLForSchedule) {
      setSelectedPDLForSchedule(null);
    }
  };

  // Actual submission logic (extracted from handleFinalSubmit)
  const proceedWithSubmission = async (followUpDates = []) => {
    setIsSubmitting(true);
    
    try {
      let hr_info_id;
      let health_consult_id;
      
      if (isEditMode) {
        // For edit mode, use the selected hr_info_id (which can be different if PDL was changed)
        hr_info_id = selectedHrInfoId || initialData?.hr_info_id || consultationData?.hr_info_id;
        health_consult_id = initialData?.id || consultationData?.id || location.state?.recordId;
      } else {
        // For create mode, use selectedHrInfoId or fallback to localStorage
        hr_info_id = selectedHrInfoId;
        if (!hr_info_id) {
          let selectedPDL = {};
          try { selectedPDL = JSON.parse(localStorage.getItem('selectedPDL') || '{}'); } catch { /* corrupted */ }
          hr_info_id = selectedPDL.id;
        }
      }

      // Get current user from localStorage
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const currentUserId = currentUser.id;

      // Prepare health_consult data
      const healthConsultData = {
        hr_info_id: hr_info_id,
        date: symptomsData.date,
        cell_dorm: null, // Field removed from frontend but backend still expects it
        chief_complaint: symptomsData.chiefComplaint,
        diagnosis: symptomsData.diagnosis || null,
        diagnostics: medicationData.diagnostics || null,
        referral: medicationData.referral || null
      };
      
      // Add the appropriate nod_md field based on create or update mode
      if (isEditMode) {
        healthConsultData.nod_md_update = currentUserId ? parseInt(currentUserId) : null;
      } else {
        healthConsultData.nod_md_create = currentUserId ? parseInt(currentUserId) : null;
      }

      // Debug: Log the data being sent
      // Submit or update health consultation data
      const consultResponse = await authFetch(
        isEditMode 
          ? `${API_BASE_URL}/api/health-consult/${health_consult_id}`
          : `${API_BASE_URL}/api/health-consult`,
        {
          method: isEditMode ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(healthConsultData),
        }
      );

      if (!consultResponse.ok) {
        let errorDetails;
        try {
          errorDetails = await consultResponse.json();
        } catch {
          errorDetails = await consultResponse.text();
        }
        console.error('Consultation submission error:', errorDetails);
        console.error('Response status:', consultResponse.status);
        
        // Show specific validation errors if available
        if (consultResponse.status === 422 && errorDetails.errors) {
          const validationErrors = Object.entries(errorDetails.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n');
          alert(`Validation Error:\n${validationErrors}`);
        } else {
          alert(`Error ${isEditMode ? 'updating' : 'submitting'} consultation: ${errorDetails.message || errorDetails}`);
        }
        throw new Error(`HTTP error in consultation submission! status: ${consultResponse.status}`);
      }

      const consultResult = await consultResponse.json();
      
      if (!isEditMode) {
        health_consult_id = consultResult.id;
      }

      // Handle medications
      let medicationPromises;
      
      if (isEditMode) {
        // For edit mode, use batch update endpoint
        const medicationPayload = {
          health_consult_id: health_consult_id,
          medications: medicationData.medications
            .filter(med => med.medicationName && med.dosage) // Only submit valid medications
            .map(medication => ({
              id: medication.id || null, // Include ID for existing medications
              name_of_medication: medication.medicationName,
              dosage: medication.dosage,
              quantity: medication.quantity || null
            }))
        };
        medicationPromises = [
          authFetch(`${API_BASE_URL}/api/medications/consultation/${health_consult_id}/update-batch`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(medicationPayload),
          })
        ];
      } else {
        // For create mode, create new medications
        medicationPromises = medicationData.medications
          .filter(med => med.medicationName && med.dosage) // Only submit valid medications
          .map(medication => {
            const medicationPayload = {
              health_consult_id: health_consult_id,
              name_of_medication: medication.medicationName,
              dosage: medication.dosage,
              quantity: medication.quantity || null
            };
            return authFetch(`${API_BASE_URL}/api/medications`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(medicationPayload),
            });
          });
      }

      // Wait for all medication submissions to complete
      const medicationResponses = await Promise.all(medicationPromises);
      
      // Check if all medication submissions were successful
      const failedMedications = medicationResponses.filter(response => !response.ok);
      if (failedMedications.length > 0) {
        console.error('Failed medication responses:', failedMedications);
        for (const failedResponse of failedMedications) {
          const errorText = await failedResponse.text();
          console.error('Medication error:', errorText);
        }
        throw new Error(`Failed to submit ${failedMedications.length} medication(s)`);
      }

      // Handle follow-up consultation dates if provided
      if (followUpDates && followUpDates.length > 0) {
        // Filter out dates that already exist in the database
        const existingDates = followUpRecords
          .map(record => {
            const date = new Date(record.date || record.date_of_followup);
            return !isNaN(date) ? date.toDateString() : null;
          })
          .filter(dateStr => dateStr !== null);
        
        const newDates = followUpDates.filter(date => 
          !existingDates.includes(date.toDateString())
        );
        // Only submit if there are new dates to add
        if (newDates.length > 0) {
          const followUpPayload = {
            hr_info_id: hr_info_id,
            dates: newDates.map(date => {
              // Format date as YYYY-MM-DD for the backend
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }),
            nod_md: currentUserId ? parseInt(currentUserId) : null
          };
          const followUpResponse = await authFetch(`${API_BASE_URL}/api/followup-consult`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(followUpPayload),
          });

          if (!followUpResponse.ok) {
            let errorDetails;
            try {
              errorDetails = await followUpResponse.json();
            } catch {
              errorDetails = await followUpResponse.text();
            }
            console.error('Follow-up consultation submission error:', errorDetails);
            // Don't throw error here - consultation was saved successfully, just log the follow-up error
            console.warn('Main consultation saved but follow-up dates failed to save');
          } else {
            await followUpResponse.json();
          }
        } else {
        }
      }

      // Delete follow-up consultation if it exists (when coming from follow-up calendar)
      const storedPDL = localStorage.getItem('selectedPDL');
      if (storedPDL) {
        try {
          const pdlData = JSON.parse(storedPDL);
          if (pdlData.followUpConsultationId) {
            try {
              const deleteResponse = await authFetch(`${API_BASE_URL}/api/followup-consult/${pdlData.followUpConsultationId}`, {
                method: 'DELETE',
                headers: {
                  'Content-Type': 'application/json',
                }
              });
              
              if (deleteResponse.ok) {
              } else {
                console.warn('Failed to delete follow-up consultation, but consultation was saved');
              }
            } catch (deleteError) {
              console.error('Error deleting follow-up consultation:', deleteError);
              // Don't throw - consultation was saved successfully
            }
          }
        } catch (parseError) {
          console.error('Error parsing PDL data:', parseError);
        }
      }

      setIsSubmitting(false);
      setSuccessTitle(isEditMode ? 'Consultation Updated Successfully' : 'Consultation Submitted Successfully');
      const followUpMessage = followUpDates && followUpDates.length > 0 
        ? ` with ${followUpDates.length} follow-up date(s)` 
        : '';
      setSuccessMessage(`Health consultation and ${medicationData.medications.filter(med => med.medicationName && med.dosage).length} medication(s) have been ${isEditMode ? 'updated' : 'saved'} successfully${followUpMessage}.`);
      setShowSuccessModal(true);
      
    } catch (error) {
      console.error('Error submitting consultation:', error);
      alert(`Error ${isEditMode ? 'updating' : 'submitting'} consultation. Please try again.`);
      setIsSubmitting(false);
    }
  };

  // Handle success modal OK button click
  const handleSuccessModalOk = () => {
    setShowSuccessModal(false);
    
    if (isEditMode) {
      // Navigate back to consultation search page after successful update
      navigate('/pdl-consultation-search');
    } else {
      // Reset forms after successful submission for create mode
      setSymptomsData({
        lastName: '',
        firstName: '',
        middleName: '',
        aka: '',
        age: '',
        date: '',
        chiefComplaint: '',
        diagnosis: ''
      });
      setMedicationData({
        medications: [{ id: null, medicationName: '', dosage: '', quantity: '' }],
        diagnostics: '',
        referral: '',
        nodMdInitials: ''
      });
      
      // Clear selected PDL
      localStorage.removeItem('selectedPDL');
      setSelectedPDLDisplay(null);
      setSelectedHrInfoId(null);
      
      setCurrentView('search'); // Return to search view
    }
  };

  // Keep original handleFinalSubmit for backward compatibility
  const handleFinalSubmit = handleSubmitClick;

  return (
    <div className={`${styles.consultWizardContainer} ${isDarkMode ? '' : styles.lightMode}`}>
      {/* System Header - Same as HR Wizard */}
      <header className={styles.systemHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.systemLogo}>
            <img src="/BJMPRO-CAR.png" alt="BJMP CAR Logo" className={styles.systemLogoImg} />
            <div className={styles.systemTitle}>
              <h1>MediCast: BJMP Health Monitoring System</h1>
              <p>Bureau of Jail Management and Penology</p>
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.systemInfo}>
            <div className={styles.systemTime}>{currentTime.toLocaleTimeString()}</div>
            <div className={styles.systemUser}>{formatDate(currentTime)}</div>
          </div>
          <NotificationBell />
          <div className={styles.profileSection}>
            <div className={styles.profileIcon} onClick={toggleDropdown}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="currentColor"/>
              </svg>
            </div>
            {isDropdownOpen && (
              <div className={styles.profileDropdown}>
                <div className={styles.dropdownHeader}>
                  <div className={styles.userInfo}>
                    <div className={styles.userRole}>
                      {userData?.user_type === 'unit_nurse' ? 'Unit Nurse' : 
                       userData?.user_type === 'chief_nurse' ? 'Chief Nurse' : 
                       userData?.user_type === 'warden' ? 'Warden' : 
                       userData?.user_type === 'admin' ? 'System Admin' : 
                       userData?.user_type === 'jail_officer' ? 'Jail Officer' : 'User'}
                    </div>
                    <div className={styles.userName}>
                      {userData ? 
                        `${userData.last_name ? userData.last_name.charAt(0).toUpperCase() + userData.last_name.slice(1).toLowerCase() : ''}, ${userData.first_name ? userData.first_name.charAt(0).toUpperCase() + userData.first_name.slice(1).toLowerCase() : ''} ${userData.middle_name ? userData.middle_name.charAt(0).toUpperCase() + userData.middle_name.slice(1).toLowerCase() : ''}`.trim() 
                        : 'User Name'}
                    </div>
                  </div>
                </div>
                <div className={styles.dropdownDivider}></div>
                <button 
                  className={styles.dropdownItem}
                  onClick={toggleDarkMode}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {isDarkMode ? (
                      <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    )}
                  </svg>
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </button>
                <button className={styles.dropdownItem} onClick={() => {
                  setShowChangePasswordModal(true);
                  setIsDropdownOpen(false);
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Change Password
                </button>
                <button className={styles.dropdownItem} onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Consultation Content Wrapper */}
      <div className={styles.consultContentWrapper}>
        {/* Sidebar Navigation */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarSection}>
            <div className={styles.sidebarTitle}>Navigation</div>
            <ul className={styles.sidebarMenu}>
              <li className={styles.sidebarItem}>
                <button
                  type="button"
                  className={styles.sidebarLink}
                  onClick={() => navigate('/')}
                  title="Dashboard"
                >
                  <svg className={styles.sidebarIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Dashboard</span>
                </button>
              </li>
            </ul>
          </div>
          
          <div className={styles.sidebarSection}>
            <div className={styles.sidebarTitle}>
              {isEditMode ? 'Edit Consultation' : 'Search & Reports'}
            </div>
            <ul className={styles.sidebarMenu}>
              <li className={styles.sidebarItem}>
                <button
                  type="button"
                  className={`${styles.sidebarLink}${currentView === 'search' ? ` ${styles.active}` : ''}`}
                  onClick={() => setCurrentView('search')}
                  title="Search Consultation Records"
                >
                  <svg className={styles.sidebarIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                    <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  <span>Search PDL</span>
                </button>
              </li>
              <li className={styles.sidebarItem}>
                <button
                  type="button"
                  className={`${styles.sidebarLink}${currentView === 'symptoms' ? ` ${styles.active}` : ''}`}
                  onClick={() => setCurrentView('symptoms')}
                  title="Consultation Symptoms Form"
                >
                  <svg className={styles.sidebarIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 11H3m3 8H3m5-13H3m13 13h-2a4 4 0 01-4-4V6a4 4 0 014-4h2v14z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M9 7h1a4 4 0 014 4v1a4 4 0 004 4h1" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  <span>{isEditMode ? 'Edit Symptoms' : 'Consultation Symptoms'}</span>
                </button>
              </li>
              <li className={styles.sidebarItem}>
                <button
                  type="button"
                  className={`${styles.sidebarLink}${currentView === 'medication' ? ` ${styles.active}` : ''}`}
                  onClick={() => setCurrentView('medication')}
                  title="Consultation Medication Form"
                >
                  <svg className={styles.sidebarIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19.5 12.5L11 21L6.5 16.5L15 8L19.5 12.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M18 11L20.5 8.5C21.3 7.7 21.3 6.3 20.5 5.5L18.5 3.5C17.7 2.7 16.3 2.7 15.5 3.5L13 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 19L9 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{isEditMode ? 'Edit Medication' : 'Consultation Medication'}</span>
                </button>
              </li>
              <li className={styles.sidebarItem}>
                <button
                  type="button"
                  className={`${styles.sidebarLink}${currentView === 'followup' ? ` ${styles.active}` : ''}`}
                  onClick={() => setCurrentView('followup')}
                  title="Schedule Follow-up Consultation"
                >
                  <svg className={styles.sidebarIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" fill="none"/>
                    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 14v4M10 16h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>Follow-up Consultation</span>
                </button>
              </li>
            </ul>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className={styles.mainContent}>
          {/* Progress Bar - hidden on followup view */}
          {currentView !== 'followup' && (
            <div className={styles.progressBarContainer}>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill}
                  style={{ 
                    width: `${Math.round(Math.max(0, Math.min(100, overallProgress)))}%` 
                  }}
                >
                  {overallProgress > 5 && (
                    <span className={styles.progressText}>
                      {Math.round(overallProgress)}%
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.progressSteps}>
                {(() => {
                  const viewOrder = ['search', 'symptoms', 'medication'];
                  const currentIdx = viewOrder.indexOf(currentView);
                  
                  // Step 1: Search PDL - complete when PDL is selected
                  const isSearchComplete = !!selectedHrInfoId;
                  const isSearchVisited = currentIdx > 0;
                  const searchStepClass = currentView === 'search'
                    ? styles.stepCurrent
                    : isSearchVisited && isSearchComplete
                      ? styles.stepCompleted
                      : isSearchVisited && !isSearchComplete
                        ? styles.stepIncomplete
                        : styles.stepPending;
                  
                  // Step 2: Symptoms - complete when date + chiefComplaint filled
                  const isSymptomsComplete = !!(symptomsData.date && symptomsData.chiefComplaint?.trim());
                  const isSymptomsVisited = currentIdx > 1;
                  const symptomsStepClass = currentView === 'symptoms'
                    ? styles.stepCurrent
                    : isSymptomsVisited && isSymptomsComplete
                      ? styles.stepCompleted
                      : isSymptomsVisited && !isSymptomsComplete
                        ? styles.stepIncomplete
                        : styles.stepPending;
                  
                  // Step 3: Medication - complete when at least one med has name + dosage
                  const isMedComplete = medicationData.medications?.length > 0 && 
                    medicationData.medications.every(med => med.medicationName?.trim() && med.dosage?.trim());
                  
                  return (
                    <>
                      <div className={styles.progressStep}>
                        <div className={`${styles.stepCircle} ${searchStepClass}`}>
                          {isSearchVisited && isSearchComplete ? '\u2713' : isSearchVisited && !isSearchComplete ? '!' : '1'}
                        </div>
                        <span className={`${styles.stepLabel} ${
                          currentView === 'search' ? styles.stepActive : ''
                        }`}>
                          Search PDL
                          {currentView === 'search' && searchProgress > 0 && (
                            <span className={styles.stepProgress}>
                              ({searchProgress}%)
                            </span>
                          )}
                        </span>
                      </div>
                      <div className={styles.progressStep}>
                        <div className={`${styles.stepCircle} ${symptomsStepClass}`}>
                          {isSymptomsVisited && isSymptomsComplete ? '\u2713' : isSymptomsVisited && !isSymptomsComplete ? '!' : '2'}
                        </div>
                        <span className={`${styles.stepLabel} ${
                          currentView === 'symptoms' ? styles.stepActive : ''
                        }`}>
                          Consultation Symptoms
                          {currentView === 'symptoms' && symptomsProgress > 0 && (
                            <span className={styles.stepProgress}>
                              ({symptomsProgress}%)
                            </span>
                          )}
                        </span>
                      </div>
                      <div className={styles.progressStep}>
                        <div className={`${styles.stepCircle} ${
                          currentView === 'medication' ? styles.stepCurrent : styles.stepPending
                        }`}>
                          {currentView === 'medication' && isMedComplete ? '\u2713' : '3'}
                        </div>
                        <span className={`${styles.stepLabel} ${
                          currentView === 'medication' ? styles.stepActive : ''
                        }`}>
                          Consultation Medication
                          {currentView === 'medication' && medicationProgress > 0 && (
                            <span className={styles.stepProgress}>
                              ({medicationProgress}%)
                            </span>
                          )}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {currentView === 'wizard' ? (
            <>
              <div className={styles.contentHeader}>
                <div className={styles.headerContent}>
                  <div className={styles.headerText}>
                    <h1 className={styles.pageTitle}>Consultation Wizard</h1>
                    <p className={styles.pageSubtitle}>Complete the consultation information step by step</p>
                  </div>
                </div>
              </div>

              <div className={styles.formContainer}>
                <div className={styles.formSection}>
                  <h3>Consultation Form</h3>
                  <p>Form content will be added here...</p>
                </div>
              </div>
            </>
          ) : currentView === 'symptoms' ? (
            <>
              <div className={styles.formContainer}>
                <ConsultSymptoms 
                  hideSystemHeader={true} 
                  formData={symptomsData}
                  setFormData={setSymptomsData}
                  isDarkMode={isDarkMode}
                />
                
                {/* Navigation Buttons for Symptoms */}
                <div className={`${styles.hrwizardNavBtns} ${styles.jbrNavButtons}`}>
                  <button 
                    onClick={clearFormData} 
                    className={styles.clearFormButton}
                    title="Clear all form data"
                  >
                    Clear Form
                  </button>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button 
                      onClick={handleNext} 
                      className={styles.nextButton}
                    >
                      Next
                      <span style={{ marginLeft: 6, fontSize: 16 }}>→</span>
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : currentView === 'medication' ? (
            <>
              <div className={styles.formContainer}>
                <ConsultMedication 
                  hideSystemHeader={true} 
                  formData={medicationData}
                  setFormData={setMedicationData}
                  isDarkMode={isDarkMode}
                />
                
                {/* Navigation Buttons for Medication */}
                <div className={`${styles.hrwizardNavBtns} ${styles.jbrNavButtons}`}>
                  <button 
                    onClick={clearFormData} 
                    className={styles.clearFormButton}
                    title="Clear all form data"
                  >
                    Clear Form
                  </button>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button 
                      onClick={handleBack} 
                      className={styles.backButton}
                      title="Go to previous step"
                    >
                      <span style={{ marginRight: 6, fontSize: 16 }}>←</span>
                      Back
                    </button>
                    
                    <button 
                      onClick={handleFinalSubmit} 
                      className={styles.submitButton}
                      disabled={isSubmitting}
                    >
                      {isSubmitting 
                        ? (isEditMode ? 'Updating...' : 'Submitting...') 
                        : (isEditMode ? 'Update Consultation' : 'Submit Consultation')
                      }
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : currentView === 'followup' ? (
            <>
              <div className={styles.formContainer}>
                <ConsultFollowup 
                  hideSystemHeader={true}
                  selectedPDL={selectedPDLDisplay}
                  isDarkMode={isDarkMode}
                  initialDate={followupDate}
                />
              </div>
            </>
          ) : (
            <>
              {/* Search Content */}

              {/* Selected PDL Display */}
              {selectedPDLDisplay && (
                <div className={styles.formContainer}>
                  <div className={styles.selectedPdlCard}>
                    <div className={styles.selectedPdlHeader}>
                      <div className={styles.selectedPdlHeaderTitle}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                          <path d="M20 8V14M23 11H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <h3>Selected PDL</h3>
                      </div>
                      <button 
                        type="button" 
                        onClick={clearSelectedPDLDisplay}
                        className={styles.clearPdlButton}
                        title="Clear selection"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    <div className={styles.selectedPdlContent}>
                      <div className={styles.selectedPdlInfo}>
                        <div className={styles.selectedPdlRow}>
                          <span className={styles.selectedPdlLabel}>Full Name:</span>
                          <span className={styles.selectedPdlValue}>{selectedPDLDisplay.fullName}</span>
                        </div>
                        <div className={styles.selectedPdlRow}>
                          <span className={styles.selectedPdlLabel}>Also Known As:</span>
                          <span className={styles.selectedPdlValue}>{selectedPDLDisplay.alsoKnownAs || 'N/A'}</span>
                        </div>
                        <div className={styles.selectedPdlRow}>
                          <span className={styles.selectedPdlLabel}>Age:</span>
                          <span className={styles.selectedPdlValue}>{selectedPDLDisplay.age}</span>
                        </div>
                        <div className={styles.selectedPdlRow}>
                          <span className={styles.selectedPdlLabel}>Current Cell:</span>
                          <span className={styles.selectedPdlValue}>{selectedPDLDisplay.currentCell || 'Unassigned'}</span>
                        </div>
                      </div>
                      <div className={styles.selectedPdlPhoto}>
                        {selectedPDLDisplay.photo ? (
                          <>
                            <img 
                              src={`/api/storage/${selectedPDLDisplay.photo}`} 
                              alt={selectedPDLDisplay.fullName}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className={styles.photoPlaceholder} style={{display: 'none'}}>
                              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                              <span>No Photo</span>
                            </div>
                          </>
                        ) : (
                          <div className={styles.photoPlaceholder}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            <span>No Photo</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Search Form Section */}
              <div className={styles.formContainer}>
                <div className={styles.formSection}>
                  <div className={styles.sectionHeader}>
                    <div className={styles.sectionIcon}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                        <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <h2 className={styles.sectionTitle}>Search Criteria</h2>
                  </div>
                  
                  <form onSubmit={handleSearch} className={styles.searchForm}>
                    <div className={styles.formGrid}>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Last Name</label>
                        <input 
                          type="text" 
                          name="lastName" 
                          value={searchData.lastName} 
                          onChange={handleInputChange} 
                          onKeyDown={lettersOnly}
                          className={styles.formInput} 
                          placeholder="Enter last name"
                          autoComplete="off" 
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>First Name</label>
                        <input 
                          type="text" 
                          name="firstName" 
                          value={searchData.firstName} 
                          onChange={handleInputChange} 
                          onKeyDown={lettersOnly}
                          className={styles.formInput} 
                          placeholder="Enter first name"
                          autoComplete="off" 
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Middle Name</label>
                        <input 
                          type="text" 
                          name="middleName" 
                          value={searchData.middleName} 
                          onChange={handleInputChange} 
                          onKeyDown={lettersOnly}
                          className={styles.formInput} 
                          placeholder="Enter middle name"
                          autoComplete="off" 
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>AKA</label>
                        <input 
                          type="text" 
                          name="aka" 
                          value={searchData.aka} 
                          onChange={handleInputChange} 
                          onKeyDown={lettersOnly}
                          className={styles.formInput} 
                          placeholder="Also Known As"
                          autoComplete="off" 
                        />
                      </div>
                    </div>
                    
                    <div className={styles.formActions}>
                      <button 
                        type="button" 
                        onClick={handleClearSearch} 
                        className={styles.clearButton}
                        disabled={isLoading}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Clear
                      </button>
                      <button 
                        type="submit" 
                        className={styles.searchButton}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <div className={styles.spinner}></div>
                            Searching...
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                              <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            Search PDL
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Search Results Section */}
                {searchResults.length > 0 && (
                  <div className={styles.resultsTableCard}>
                    <div className={styles.tableCardHeader}>
                      <h3>Search Results</h3>
                      <span className={styles.recordCount}>Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, searchResults.length)} of {searchResults.length}</span>
                    </div>
                    
                    <div className={styles.tableWrapper}>
                      <table className={styles.resultsTable}>
                        <thead>
                          <tr>
                            <th>Current Cell</th>
                            <th>Last Name</th>
                            <th>First Name</th>
                            <th>Middle Name</th>
                            <th>AKA</th>
                            <th>Age</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((record) => (
                            <tr key={record.id}>
                              <td>
                                <span className={styles.cellBadge}>
                                  {record.current_cell || 'UNASSIGNED'}
                                </span>
                              </td>
                              <td>{record.last_name}</td>
                              <td>{record.first_name}</td>
                              <td>{record.middle_name}</td>
                              <td>{record.aka || 'N/A'}</td>
                              <td>{calculateAge(record.date_of_birth)}</td>
                              <td>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                  <button 
                                    onClick={() => handleSelectRecord(record)} 
                                    className={styles.selectButton}
                                    title="Select Record"
                                    disabled={pdlDetailsLoading}
                                  >
                                    {pdlDetailsLoading ? (
                                      <>
                                        <div className={styles.spinner}></div>
                                        Loading...
                                      </>
                                    ) : (
                                      <>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                                        </svg>
                                        Select
                                      </>
                                    )}
                                  </button>
                                  <button 
                                    onClick={() => handleScheduleCheckup(record)} 
                                    className={styles.scheduleButton}
                                    title="Schedule Checkup"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Schedule
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className={styles.paginationContainer}>
                        <button 
                          onClick={handlePrevPage}
                          disabled={currentPage === 1}
                          className={styles.paginationButton}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Previous
                        </button>
                        
                        <div className={styles.paginationNumbers}>
                          {[...Array(totalPages)].map((_, index) => {
                            const pageNumber = index + 1;
                            // Show first page, last page, current page, and pages around current
                            if (
                              pageNumber === 1 ||
                              pageNumber === totalPages ||
                              (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                            ) {
                              return (
                                <button
                                  key={pageNumber}
                                  onClick={() => handlePageChange(pageNumber)}
                                  className={`${styles.paginationNumber} ${currentPage === pageNumber ? styles.active : ''}`}
                                >
                                  {pageNumber}
                                </button>
                              );
                            } else if (
                              pageNumber === currentPage - 2 ||
                              pageNumber === currentPage + 2
                            ) {
                              return <span key={pageNumber} className={styles.paginationEllipsis}>...</span>;
                            }
                            return null;
                          })}
                        </div>
                        
                        <button 
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className={styles.paginationButton}
                        >
                          Next
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Loading Overlay */}
      {isSubmitting && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinnerLarge}></div>
            <div className={styles.loadingText}>
              <h3>Submitting Consultation...</h3>
              <p>Please wait while we process your data</p>
              <div className={styles.loadingProgress}>
                <div className={styles.progressBar}>
                  <div className={styles.progressFill}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className={styles.modalOverlay} onClick={handleSuccessModalOk}>
          <div className={styles.successModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.successIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>{successTitle}</h3>
            <p>{successMessage}</p>
            <button className={styles.successBtn} onClick={handleSuccessModalOk}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Schedule Checkup Success Modal */}
      {showScheduleSuccessModal && scheduleSuccessData && (
        <div className={styles.successModalOverlay}>
          <div className={styles.successModalContent}>
            <div className={styles.successModalHeader}>
              <div className={styles.successIcon}>✅</div>
              <h3>Checkup Scheduled Successfully</h3>
            </div>
            <div className={styles.successModalBody}>
              <p className={styles.successMessage}>
                Checkup has been scheduled for <strong>{scheduleSuccessData.pdlName}</strong>
              </p>
              <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#60a5fa' }}>Scheduled Dates:</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {scheduleSuccessData.dates.map((date, index) => (
                    <li key={index} style={{ padding: '0.25rem 0', color: '#e2e8f0' }}>
                      📅 {date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className={styles.successModalActions}>
              <button 
                className={styles.btnSuccessOk}
                onClick={() => {
                  setShowScheduleSuccessModal(false);
                  setScheduleSuccessData(null);
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Validation Modal */}
      {showValidationModal && (
        <div className={styles.validationModalOverlay}>
          <div className={styles.validationModalContent}>
            <div className={styles.validationModalHeader}>
              <div className={styles.validationWarningIcon}>⚠️</div>
              <h3>Required Fields Missing</h3>
            </div>
            <div className={styles.validationModalBody}>
              <div className={styles.validationMessage}>
                Please fill in the following required fields:
              </div>
              <div className={styles.validationSectionInfo}>
                <span className={styles.sectionLabel}>Section:</span>
                <span className={styles.sectionName}>{missingFields[0]?.section}</span>
              </div>
              <div className={styles.missingFieldsContainer}>
                <ul className={styles.missingFieldsList}>
                  {missingFields.map((field, index) => (
                    <li key={index} className={styles.missingFieldItem}>
                      <span className={styles.fieldBullet}>•</span>
                      <span className={styles.fieldName}>{field.field}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className={styles.validationModalActions}>
              <button 
                onClick={() => goToSection(missingFields[0]?.section)}
                className={styles.btnGoToSection}
              >
                Go to {missingFields[0]?.section}
              </button>
              <button 
                onClick={() => setShowValidationModal(false)}
                className={styles.btnCloseValidation}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clear Form Confirmation Modal */}
      {clearFormModal.show && (
        <div className={styles.clearFormModalOverlay}>
          <div className={styles.clearFormModalContent}>
            <div className={styles.clearFormModalHeader}>
              <div className={styles.clearFormWarningIcon}>⚠️</div>
              <h3>Clear All Form Data</h3>
            </div>
            <div className={styles.clearFormModalBody}>
              <p className={styles.clearFormMessage}>
                Are you sure you want to clear all consultation form data? This action cannot be undone.
              </p>
              <div className={styles.clearFormWarning}>
                <span className={styles.warningBullet}>•</span>
                <span>All entered information will be permanently lost</span>
              </div>
              <div className={styles.clearFormWarning}>
                <span className={styles.warningBullet}>•</span>
                <span>You will need to start over from the beginning</span>
              </div>
            </div>
            <div className={styles.clearFormModalActions}>
              <button 
                className={styles.btnCancelClear}
                onClick={cancelClearFormData}
              >
                Cancel
              </button>
              <button 
                className={styles.btnConfirmClear}
                onClick={confirmClearFormData}
              >
                Clear Form
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Validation Modal */}
      {showSearchValidationModal && (
        <div className={styles.searchValidationModalOverlay}>
          <div className={styles.searchValidationModalContent}>
            <div className={styles.searchValidationModalHeader}>
              <div className={styles.searchValidationIcon}>⚠️</div>
              <h3>Search Criteria Required</h3>
            </div>
            <div className={styles.searchValidationModalBody}>
              <div className={styles.searchValidationMessage}>
                Please enter at least one search criteria to continue:
              </div>
              <div className={styles.searchFieldsContainer}>
                <ul className={styles.searchFieldsList}>
                  <li className={styles.searchFieldItem}>
                    <span className={styles.searchFieldBullet}>•</span>
                    <span className={styles.searchFieldName}>Last Name</span>
                  </li>
                  <li className={styles.searchFieldItem}>
                    <span className={styles.searchFieldBullet}>•</span>
                    <span className={styles.searchFieldName}>First Name</span>
                  </li>
                  <li className={styles.searchFieldItem}>
                    <span className={styles.searchFieldBullet}>•</span>
                    <span className={styles.searchFieldName}>Middle Name</span>
                  </li>
                  <li className={styles.searchFieldItem}>
                    <span className={styles.searchFieldBullet}>•</span>
                    <span className={styles.searchFieldName}>AKA (Also Known As)</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className={styles.searchValidationModalActions}>
              <button 
                onClick={() => setShowSearchValidationModal(false)}
                className={styles.btnCloseSearchValidation}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDL Selection Confirmation Modal */}
      {showPDLConfirmModal && selectedPDLForConfirm && (
        <div className={styles.modalOverlay}>
          <div className={styles.confirmModalContent}>
            <div className={styles.modalHeader}>
              <h3>Confirm PDL Selection</h3>
              <button onClick={cancelPDLSelection} className={styles.closeButton}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.confirmMessage}>
                <p>Are you sure you want to select this PDL for consultation?</p>
              </div>
              
              <div className={styles.pdlDetailsCard}>
                {/* PDL Photo Section */}
                <div className={styles.pdlPhotoSection}>
                  {selectedPDLForConfirm.photo_of_pdl ? (
                    <img 
                      src={`/api/storage/${selectedPDLForConfirm.photo_of_pdl}`} 
                      alt="PDL"
                      className={styles.pdlPhoto}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={styles.pdlPhotoPlaceholder} style={{display: selectedPDLForConfirm.photo_of_pdl ? 'none' : 'flex'}}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <span>No Photo</span>
                  </div>
                </div>

                {/* PDL Details */}
                <div className={styles.pdlDetailsGrid}>
                  <div className={styles.pdlDetailItem}>
                    <label>Full Name:</label>
                    <span>
                      {selectedPDLForConfirm.last_name}, {selectedPDLForConfirm.first_name} 
                      {selectedPDLForConfirm.middle_name && ` ${selectedPDLForConfirm.middle_name}`}
                    </span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>AKA / Alias:</label>
                    <span>{selectedPDLForConfirm.alias_nickname || selectedPDLForConfirm.aka || 'N/A'}</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Sex:</label>
                    <span>{selectedPDLForConfirm.sex === 'M' ? 'Male' : selectedPDLForConfirm.sex === 'F' ? 'Female' : selectedPDLForConfirm.sex || 'N/A'}</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Age:</label>
                    <span>{calculateAge(selectedPDLForConfirm.date_of_birth)} years old</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Civil Status:</label>
                    <span>{selectedPDLForConfirm.civil_status || 'N/A'}</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Current Cell:</label>
                    <span>
                      {selectedPDLForConfirm.cell?.cell_no || 'Unassigned'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className={styles.confirmActions}>
                <button 
                  onClick={cancelPDLSelection} 
                  className={styles.cancelConfirmButton}
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmPDLSelection} 
                  className={styles.confirmSelectButton}
                >
                  Confirm Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Checkup Modal */}
      {showScheduleModal && selectedPDLForSchedule && (
        <div className={styles.modalOverlay}>
          <div className={styles.confirmModalContent}>
            <div className={styles.modalHeader}>
              <h3>Schedule Checkup</h3>
              <button 
                onClick={cancelScheduleCheckup}
                className={styles.closeButton}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.confirmMessage}>
                <p>Schedule a checkup for this PDL?</p>
              </div>
              
              <div className={styles.pdlDetailsCard}>
                {/* PDL Photo Section */}
                <div className={styles.pdlPhotoSection}>
                  {selectedPDLForSchedule.photo_of_pdl ? (
                    <img 
                      src={`/api/storage/${selectedPDLForSchedule.photo_of_pdl}`} 
                      alt="PDL"
                      className={styles.pdlPhoto}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={styles.pdlPhotoPlaceholder} style={{display: selectedPDLForSchedule.photo_of_pdl ? 'none' : 'flex'}}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <span>No Photo</span>
                  </div>
                </div>

                {/* PDL Details */}
                <div className={styles.pdlDetailsGrid}>
                  <div className={styles.pdlDetailItem}>
                    <label>Full Name:</label>
                    <span>
                      {selectedPDLForSchedule.last_name}, {selectedPDLForSchedule.first_name} 
                      {selectedPDLForSchedule.middle_name && ` ${selectedPDLForSchedule.middle_name}`}
                    </span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>AKA / Alias:</label>
                    <span>{selectedPDLForSchedule.alias_nickname || selectedPDLForSchedule.aka || 'N/A'}</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Sex:</label>
                    <span>{selectedPDLForSchedule.sex === 'M' ? 'Male' : selectedPDLForSchedule.sex === 'F' ? 'Female' : selectedPDLForSchedule.sex || 'N/A'}</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Age:</label>
                    <span>{calculateAge(selectedPDLForSchedule.date_of_birth)} years old</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Civil Status:</label>
                    <span>{selectedPDLForSchedule.civil_status || 'N/A'}</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Current Cell:</label>
                    <span>
                      {selectedPDLForSchedule.cell?.cell_no || 'Unassigned'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className={styles.confirmActions}>
                <button 
                  onClick={cancelScheduleCheckup} 
                  className={styles.cancelConfirmButton}
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmScheduleCheckup} 
                  className={styles.confirmSelectButton}
                >
                  Schedule Checkup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selection Success Modal */}
      {showSelectionSuccessModal && (
        <div className={styles.selectionSuccessModalOverlay}>
          <div className={styles.selectionSuccessModalContent}>
            <div className={styles.selectionSuccessModalHeader}>
              <div className={styles.selectionSuccessIcon}>✅</div>
              <h3>PDL Selected Successfully</h3>
            </div>
            <div className={styles.selectionSuccessModalBody}>
              <div className={styles.selectionSuccessMessage}>
                <strong>Selected PDL:</strong> {selectionSuccessData.name}
              </div>
              <div className={styles.selectionSuccessDetails}>
                {selectionSuccessData.message}
              </div>
            </div>
            <div className={styles.selectionSuccessModalActions}>
              <button 
                onClick={() => setShowSelectionSuccessModal(false)}
                className={styles.btnCloseSelectionSuccess}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Follow-up Consultation Modal */}
      {showFollowUpModal && (
        <div className={styles.followUpModalOverlay}>
          <div className={styles.followUpModalContent}>
            <div className={styles.followUpModalHeader}>
              <button 
                onClick={() => setShowFollowUpModal(false)} 
                className={styles.followUpCloseButton}
                title="Close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className={styles.followUpIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 14v4M10 16h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Follow-up Consultation</h3>
            </div>
            <div className={styles.followUpModalBody}>
              <div className={styles.followUpMessage}>
                Would you like to schedule a follow-up consultation for this PDL?
              </div>
              <div className={styles.followUpDescription}>
                You can add follow-up details or proceed to submit the current consultation.
              </div>
            </div>
            <div className={styles.followUpModalActions}>
              <button 
                onClick={() => handleFollowUpResponse(false)}
                className={styles.btnNoFollowUp}
              >
                No, Submit Now
              </button>
              <button 
                onClick={() => handleFollowUpResponse(true)}
                className={styles.btnYesFollowUp}
              >
                Yes, Add Follow-up
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Follow-up Date Picker Modal */}
      {showFollowUpDateModal && (() => {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'];
        
        const currentMonth = monthNames[followUpCurrentDate.getMonth()];
        const currentYear = followUpCurrentDate.getFullYear();
        
        const firstDayOfMonth = new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth(), 1);
        const lastDayOfMonth = new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth() + 1, 0);
        const firstDayWeekday = firstDayOfMonth.getDay();
        const daysInMonth = lastDayOfMonth.getDate();
        const prevMonthLastDay = new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth(), 0).getDate();
        const prevMonthDays = firstDayWeekday;
        const totalCells = 42;
        
        const calendarDays = [];
        
        for (let i = prevMonthDays - 1; i >= 0; i--) {
          calendarDays.push({
            day: prevMonthLastDay - i,
            isCurrentMonth: false,
            date: new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth() - 1, prevMonthLastDay - i)
          });
        }
        
        for (let i = 1; i <= daysInMonth; i++) {
          calendarDays.push({
            day: i,
            isCurrentMonth: true,
            date: new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth(), i)
          });
        }
        
        const remainingCells = totalCells - calendarDays.length;
        for (let i = 1; i <= remainingCells; i++) {
          calendarDays.push({
            day: i,
            isCurrentMonth: false,
            date: new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth() + 1, i)
          });
        }
        
        const isToday = (date) => {
          const today = new Date();
          return date.getDate() === today.getDate() &&
                 date.getMonth() === today.getMonth() &&
                 date.getFullYear() === today.getFullYear();
        };
        
        const isPastOrToday = (date) => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const checkDate = new Date(date);
          checkDate.setHours(0, 0, 0, 0);
          return checkDate <= today;
        };
        
        const isSelected = (date) => {
          return followUpSelectedDates.some(selectedDate => 
            date.getDate() === selectedDate.getDate() &&
            date.getMonth() === selectedDate.getMonth() &&
            date.getFullYear() === selectedDate.getFullYear()
          );
        };
        
        return (
          <div className={styles.followUpDateModalOverlay}>
            <div className={styles.followUpDateModalContent}>
              <button 
                className={styles.followUpCloseButton}
                onClick={handleFollowUpDateCancel}
                aria-label="Close modal"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              
              <div className={styles.followUpDateHeader}>
                <div className={styles.followUpModalIcon}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h3>Select Follow-up Date</h3>
                <p>Choose a date for the follow-up consultation</p>
              </div>
              
              <div className={styles.followUpCalendarWrapper}>
                <div className={styles.followUpCalendarMainContent}>
                  <div className={styles.followUpCalendarLeft}>
                    <div className={styles.followUpCalendarHeader}>
                      <h4>{currentMonth} {currentYear}</h4>
                      <div className={styles.followUpNavButtons}>
                        <button 
                          className={styles.followUpBtnNav}
                          onClick={() => setFollowUpCurrentDate(new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth() - 1, 1))}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <button 
                          className={styles.followUpBtnNav}
                          onClick={() => setFollowUpCurrentDate(new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth() + 1, 1))}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                    
                    <div className={styles.followUpCalendarGrid}>
                      <div className={styles.followUpDayHeaders}>
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                          <div key={day} className={styles.followUpDayHeader}>{day}</div>
                        ))}
                      </div>
                      
                      <div className={styles.followUpDaysGrid}>
                        {calendarDays.map((dayObj, index) => {
                          const isDisabled = isPastOrToday(dayObj.date);
                          return (
                            <div
                              key={index}
                              className={`${styles.followUpDayCell} ${
                                !dayObj.isCurrentMonth ? styles.otherMonth : ''
                              } ${
                                isToday(dayObj.date) ? styles.today : ''
                              } ${
                                isSelected(dayObj.date) ? styles.selected : ''
                              } ${
                                isDisabled ? styles.disabled : ''
                              }`}
                              onClick={() => !isDisabled && toggleDateSelection(dayObj.date)}
                              style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                            >
                              <span className={styles.followUpDayNumber}>{dayObj.day}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.followUpCalendarRight}>
                    <div className={styles.followUpSelectedInfo}>
                      <div className={styles.followUpSelectedHeader}>
                        <div className={styles.followUpSelectedLabel}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 11l3 3L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Selected Dates ({followUpSelectedDates.length})
                        </div>
                        {followUpSelectedDates.length > 0 && (
                          <button 
                            className={styles.btnClearAllDates}
                            onClick={clearAllSelectedDates}
                            title="Clear all dates"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Clear All
                          </button>
                        )}
                      </div>
                      
                      <div className={styles.followUpSelectedDatesScroll}>
                        {followUpSelectedDates.length === 0 ? (
                          <div className={styles.followUpNoSelection}>
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                              <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            <p>No dates selected</p>
                            <span>Click on dates in the calendar to select</span>
                          </div>
                        ) : (
                          <div className={styles.followUpSelectedDatesList}>
                            {[...followUpSelectedDates].sort((a, b) => a - b).map((date, index) => (
                              <div key={index} className={styles.followUpSelectedDateItem}>
                                <div className={styles.followUpDateItemContent}>
                                  <div className={styles.followUpDateItemIcon}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                                      <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                  </div>
                                  <div className={styles.followUpDateItemText}>
                                    <div className={styles.followUpDateItemDay}>
                                      {date.toLocaleDateString('en-US', { weekday: 'long' })}
                                    </div>
                                    <div className={styles.followUpDateItemFull}>
                                      {date.toLocaleDateString('en-US', { 
                                        year: 'numeric', 
                                        month: 'long', 
                                        day: 'numeric' 
                                      })}
                                    </div>
                                  </div>
                                </div>
                                <button 
                                  className={styles.btnRemoveDate}
                                  onClick={() => toggleDateSelection(date)}
                                  title="Remove this date"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className={styles.followUpDateActions}>
                <button 
                  className={styles.btnFollowUpDateCancel}
                  onClick={handleFollowUpDateCancel}
                >
                  Cancel
                </button>
                <button 
                  className={styles.btnFollowUpDateConfirm}
                  onClick={handleFollowUpDateConfirm}
                  disabled={followUpSelectedDates.length === 0}
                >
                  Confirm & Submit ({followUpSelectedDates.length} {followUpSelectedDates.length === 1 ? 'date' : 'dates'})
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      
      {/* Theme Change Confirmation Modal */}
      {showThemeModal && (
        <div className={styles.modalOverlay} onClick={cancelThemeChange}>
          <div className={styles.themeModal} onClick={(e) => e.stopPropagation()} style={{ borderColor: '#f59e0b' }}>
            <div className={styles.themeIcon} style={{ background: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.3)', color: '#f59e0b' }}>
              {pendingTheme ? (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <h3 style={{ color: '#f59e0b' }}>Change Theme</h3>
            <p>Are you sure you want to switch to <strong>{pendingTheme ? 'Dark' : 'Light'} Mode</strong>?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className={styles.cancelBtn} onClick={cancelThemeChange}>Cancel</button>
              <button className={styles.themeBtn} onClick={confirmThemeChange} style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        userData={userData}
        onSuccess={(message) => {
          setSuccessMessage(message || 'Password changed successfully');
          setShowSuccessModal(true);
        }}
        onError={(message) => {
          setErrorMessage(message);
          setShowErrorModal(true);
        }}
      />

      {/* Error Modal */}
      {showErrorModal && (
        <div className={styles.modalOverlay} onClick={() => setShowErrorModal(false)}>
          <div className={styles.errorModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.errorIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Error</h3>
            <p>{errorMessage}</p>
            <button className={styles.errorBtn} onClick={() => setShowErrorModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className={styles.modalOverlay} onClick={cancelLogout}>
          <div className={styles.themeModal} onClick={(e) => e.stopPropagation()} style={{ borderColor: '#ef4444' }}>
            <div className={styles.themeIcon} style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 style={{ color: '#ef4444' }}>Logout</h3>
            <p>Are you sure you want to logout?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className={styles.cancelBtn} onClick={cancelLogout}>Cancel</button>
              <button className={styles.themeBtn} onClick={confirmLogout} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>Logout</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConsultWizard;